import React, { useEffect, useRef, useState } from 'react';
import { publicApi } from '../../api/api';
import styles from './PaymentStatusModal.module.css';

// How long we wait for the order to reach a terminal state before telling
// the customer we couldn't confirm yet (the webhook may still land later).
export const PAYMENT_STATUS_WINDOW_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;
// After this long without a result, offer "try another app" — we can't
// reliably know from the browser whether the UPI app actually opened.
const HINT_AFTER_MS = 20 * 1000;

function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Waiting-for-payment modal shown after a UPI app was launched (Cashfree).
 *
 * Source of truth is our own order status (webhook-driven), never whether the
 * app "looked" installed from the browser. Polls /orders/:id/status every 3s
 * for up to 5 minutes counted from `startedAt` (so a page reload resumes with
 * the time that's actually left, not a fresh 5 minutes).
 *
 *  - 'success' -> onSuccess()
 *  - 'failed'  -> onFailed()
 *  - window elapsed with order still pending -> "couldn't confirm" state
 *    (NOT a failure — the payment may still complete, WhatsApp confirms it)
 *  - Cancel / Try another app -> onCancel() (stops polling, back to methods)
 */
function PaymentStatusModal({ orderId, appLabel, startedAt, onSuccess, onFailed, onCancel }) {
  const deadline = startedAt + PAYMENT_STATUS_WINDOW_MS;
  const [now, setNow] = useState(Date.now());

  // Latest callbacks without restarting the polling effect on every render.
  const cbRef = useRef({ onSuccess, onFailed });
  cbRef.current = { onSuccess, onFailed };

  const timedOut = now >= deadline;

  // 1s ticker for the countdown / hint.
  useEffect(() => {
    if (timedOut) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timedOut]);

  // Status polling.
  useEffect(() => {
    if (!orderId || Date.now() >= deadline) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await publicApi.get(`/orders/${orderId}/status`);
        if (cancelled) return;
        const status = res.data?.data?.status;
        if (status === 'success') {
          cbRef.current.onSuccess();
          return;
        }
        if (status === 'failed') {
          cbRef.current.onFailed();
          return;
        }
      } catch (_) {
        // transient network/API error — keep polling until the window ends
      }
      if (cancelled || Date.now() >= deadline) return;
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId, deadline]);

  const showHint = !timedOut && now - startedAt >= HINT_AFTER_MS;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-live="polite">
        {!timedOut ? (
          <>
            <div className={styles.spinner} />
            <h3 className={styles.title}>Waiting for your payment</h3>
            <p className={styles.msg}>
              Complete the payment in <strong>{appLabel || 'your UPI app'}</strong>. This page updates automatically.
            </p>
            <div className={styles.timer}>{formatRemaining(deadline - now)}</div>
            {showHint && (
              <p className={styles.hint}>Didn't the app open? You can cancel and choose another app or method.</p>
            )}
            <button className={styles.secondaryBtn} onClick={onCancel}>
              {showHint ? 'Try another app' : 'Cancel'}
            </button>
          </>
        ) : (
          <>
            <div className={styles.icon}>⏳</div>
            <h3 className={styles.title}>We couldn't confirm your payment yet</h3>
            <p className={styles.msg}>
              If money was deducted, your order will be confirmed automatically and we'll send you a WhatsApp message.
              Otherwise you can try again.
            </p>
            <button className={styles.primaryBtn} onClick={onCancel}>Back to payment options</button>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentStatusModal;
